import { workspaceBottomInset } from "./workspace-layout";
import type { TLAsset, TLImageShape, TLShape } from "@tldraw/tlschema";

export type CreativeMode = "image" | "video";
export type GuideArea = { x: number; y: number; w: number; h: number };
export type CreativeDraft = {
  prompt: string;
  videoPrompt: string;
  references: string[];
  area: GuideArea | null;
  size: "1K" | "2K" | "4K";
  duration: "5" | "10";
  resolution: "480p" | "720p";
  audio: boolean;
  endImage: string;
};

export const motionPrompts = {
  "Slow push":
    "Move slowly toward the subject. Keep the architecture and lighting consistent.",
  "Gentle pan":
    "Pan gently across the scene. Keep the architecture and lighting consistent.",
  "Still camera": "Keep the camera still. Add only subtle ambient movement.",
};

export const imagePrompts = {
  "3D view":
    "Turn this plan into a 3D cutaway axonometric view of the same space, seen from above at about 45 degrees with the walls cut at mid-height. Keep the exact layout, furniture, doors and windows. No text, labels or dimensions.",
  Materials:
    "Explore warm oak finishes. Keep the openings, furniture and camera angle.",
  Furnish:
    "Explore natural upholstery and furnishings. Keep the room layout and camera angle.",
  Lighting:
    "Explore soft evening lighting. Keep the architecture and camera angle.",
};

/** A starter replaces an empty prompt or another starter, but never discards the user's own words. */
export function withPromptStarter(
  current: string,
  starter: string,
  starters: Record<string, string> = imagePrompts,
) {
  const text = current.trim();
  if (!text || Object.values(starters).includes(text)) return starter;
  if (text.includes(starter)) return current;
  return `${text}\n${starter}`;
}

export function newCreativeDraft(): CreativeDraft {
  return {
    prompt: "",
    videoPrompt: motionPrompts["Slow push"],
    references: [],
    area: null,
    size: "2K",
    duration: "5",
    resolution: "720p",
    audio: false,
    endImage: "",
  };
}

/** Only ready, private raster inputs. PDF/CAD/document previews need explicit flattening later. */
export function isCreativeImage(
  shape: TLShape | undefined | null,
  asset: TLAsset | undefined | null,
  room: string,
): shape is TLImageShape {
  return !!(
    shape?.type === "image" &&
    asset?.type === "image" &&
    !asset.meta.pdfPages &&
    !asset.meta.cadThumbnail &&
    !asset.meta.documentSource &&
    !asset.props.isAnimated &&
    ["image/png", "image/jpeg", "image/webp"].includes(
      asset.props.mimeType || "",
    ) &&
    asset.props.src?.startsWith(`/api/rooms/${room}/files/`) &&
    /^\/api\/rooms\/[\w-]+\/files\/[\w-]+$/.test(asset.props.src)
  );
}

/** Moving the object keeps the draft; replacing/cropping/flipping its input starts a new one. */
export function creativeSourceKey(shape: TLImageShape) {
  return JSON.stringify([
    shape.id,
    shape.props.assetId,
    shape.props.crop,
    shape.props.flipX,
    shape.props.flipY,
  ]);
}

/** Never decode a large legacy original just to show an extra guidance thumbnail. */
export function creativePreviewSource(asset: TLAsset): string | null {
  if (asset.type !== "image" || !asset.props.src) return null;
  if (asset.meta.imagePreviews) return `${asset.props.src}?size=1024`;
  return Math.max(asset.props.w, asset.props.h) <= 1024
    ? asset.props.src
    : null;
}

/** Small reference tiles use the 256px preview; never the original of a large legacy image. */
export function creativeThumbSource(asset: TLAsset): string | null {
  if (asset.type !== "image" || !asset.props.src) return null;
  if (asset.meta.imagePreviews) return `${asset.props.src}?size=256`;
  return creativePreviewSource(asset);
}

export function guideArea(
  a: { x: number; y: number },
  b: { x: number; y: number },
): GuideArea | null {
  const clamp = (v: number) =>
    Math.max(0, Math.min(1, Number.isFinite(v) ? v : 0));
  const x = Math.min(clamp(a.x), clamp(b.x)),
    y = Math.min(clamp(a.y), clamp(b.y));
  const w = Math.abs(clamp(a.x) - clamp(b.x)),
    h = Math.abs(clamp(a.y) - clamp(b.y));
  return w < 0.01 || h < 0.01 ? null : { x, y, w, h };
}

export function creativePlacement(
  view: { w: number; h: number },
  anchor: { x: number; bottom: number; right: number },
  height: number,
) {
  const width = Math.max(240, Math.min(420, view.w - 32));
  const topLimit = Math.min(150, view.h * 0.25);
  const maxHeight = Math.max(
    120,
    view.h - topLimit - workspaceBottomInset(view.w),
  );
  const h = Math.min(height, maxHeight);
  const below =
    anchor.bottom + 14 >= topLimit &&
    anchor.bottom + 14 + h < view.h - workspaceBottomInset(view.w);
  const beside = !below && anchor.right + width + 30 < view.w;
  const leftOfImage = anchor.x * 2 - anchor.right - width - 14;
  const before = !below && !beside && leftOfImage >= 100;
  const top = below
    ? anchor.bottom + 14
    : Math.max(topLimit, view.h - h - workspaceBottomInset(view.w));
  return {
    left: below
      ? Math.max(16, Math.min(view.w - width - 16, anchor.x - width / 2))
      : beside
        ? anchor.right + 14
        : before
          ? leftOfImage
          : Math.max(16, view.w - width - 16),
    top,
    width,
    maxHeight: Math.max(
      80,
      Math.min(maxHeight, view.h - top - workspaceBottomInset(view.w)),
    ),
    docked: !below && !beside && !before,
  };
}
